import React, { useState, useMemo } from 'react';
import './MetadataDiff.css';
import { ChevronRight, ChevronDown, Equal, Plus, Minus, ArrowRightLeft } from 'lucide-react';

/**
 * Keys to skip when diffing (internal/duplicate fields)
 */
const SKIP_KEYS = new Set([
    'videoMetadata', 'extractionMethod', 'isAIGenerated',
    'aiGenerator', 'aiPrompt', 'confidence',
]);

/**
 * Compute a flat diff between two metadata objects
 */
function computeDiff(before, after, prefix = '') {
    const diffs = [];
    const allKeys = new Set([
        ...Object.keys(before || {}),
        ...Object.keys(after || {}),
    ]);

    for (const key of allKeys) {
        if (SKIP_KEYS.has(key)) continue;
        const fullKey = prefix ? `${prefix}.${key}` : key;
        const bVal = before?.[key];
        const aVal = after?.[key];

        // Both are objects — recurse
        if (bVal && aVal && typeof bVal === 'object' && typeof aVal === 'object' && !Array.isArray(bVal) && !Array.isArray(aVal)) {
            diffs.push(...computeDiff(bVal, aVal, fullKey));
            continue;
        }

        const bStr = formatValue(bVal);
        const aStr = formatValue(aVal);

        if (bVal === undefined && aVal !== undefined) {
            diffs.push({ key: fullKey, type: 'added', before: null, after: aStr });
        } else if (bVal !== undefined && aVal === undefined) {
            diffs.push({ key: fullKey, type: 'removed', before: bStr, after: null });
        } else if (bStr !== aStr) {
            diffs.push({ key: fullKey, type: 'changed', before: bStr, after: aStr });
        } else {
            diffs.push({ key: fullKey, type: 'same', before: bStr, after: aStr });
        }
    }

    return diffs;
}

function formatValue(val) {
    if (val === undefined || val === null) return '';
    if (Array.isArray(val)) return JSON.stringify(val, null, 0);
    if (typeof val === 'object') return JSON.stringify(val, null, 0);
    return String(val);
}

function MetadataDiff({ before, after }) {
    const [showUnchanged, setShowUnchanged] = useState(false);

    const diffs = useMemo(() => computeDiff(before, after), [before, after]);

    const changed = diffs.filter(d => d.type === 'changed');
    const added = diffs.filter(d => d.type === 'added');
    const removed = diffs.filter(d => d.type === 'removed');
    const same = diffs.filter(d => d.type === 'same');

    const stats = {
        changed: changed.length,
        added: added.length,
        removed: removed.length,
        unchanged: same.length,
    };

    return (
        <div className="diff-container">
            <div className="diff-header">
                <span className="diff-title">Metadata Diff</span>
                <div className="diff-stats">
                    {stats.changed > 0 && (
                        <span className="diff-stat diff-stat-changed">
                            <ArrowRightLeft size={10} /> {stats.changed} changed
                        </span>
                    )}
                    {stats.added > 0 && (
                        <span className="diff-stat diff-stat-added">
                            <Plus size={10} /> {stats.added} added
                        </span>
                    )}
                    {stats.removed > 0 && (
                        <span className="diff-stat diff-stat-removed">
                            <Minus size={10} /> {stats.removed} removed
                        </span>
                    )}
                </div>
            </div>

            <div className="diff-list">
                {/* Changed */}
                {changed.length > 0 && (
                    <DiffSection title="Changed" items={changed} type="changed" />
                )}

                {/* Added */}
                {added.length > 0 && (
                    <DiffSection title="Added (After only)" items={added} type="added" />
                )}

                {/* Removed */}
                {removed.length > 0 && (
                    <DiffSection title="Removed (Before only)" items={removed} type="removed" />
                )}

                {/* Unchanged toggle */}
                {same.length > 0 && (
                    <div className="diff-section">
                        <button
                            className="diff-section-toggle"
                            onClick={() => setShowUnchanged(!showUnchanged)}
                        >
                            {showUnchanged ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            <Equal size={10} />
                            <span>Unchanged ({same.length})</span>
                        </button>
                        {showUnchanged && (
                            <div className="diff-items">
                                {same.map((d, i) => (
                                    <DiffRow key={i} diff={d} />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function DiffSection({ title, items, type }) {
    const [expanded, setExpanded] = useState(true);
    return (
        <div className="diff-section">
            <button
                className="diff-section-toggle"
                onClick={() => setExpanded(!expanded)}
            >
                {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <span className={`diff-section-label diff-section-${type}`}>{title}</span>
                <span className="diff-section-count">{items.length}</span>
            </button>
            {expanded && (
                <div className="diff-items">
                    {items.map((d, i) => (
                        <DiffRow key={i} diff={d} />
                    ))}
                </div>
            )}
        </div>
    );
}

function DiffRow({ diff }) {
    const shortKey = diff.key.includes('.') ? diff.key.split('.').pop() : diff.key;
    const path = diff.key.includes('.') ? diff.key.substring(0, diff.key.lastIndexOf('.')) : null;

    return (
        <div className={`diff-row diff-row-${diff.type}`}>
            <div className="diff-key">
                {path && <span className="diff-path">{path}.</span>}
                <span className="diff-key-name">{shortKey}</span>
            </div>
            <div className="diff-values">
                {diff.type === 'changed' && (
                    <>
                        <span className="diff-val diff-val-before">{diff.before}</span>
                        <ArrowRightLeft size={10} className="diff-arrow" />
                        <span className="diff-val diff-val-after">{diff.after}</span>
                    </>
                )}
                {diff.type === 'added' && (
                    <span className="diff-val diff-val-after">{diff.after}</span>
                )}
                {diff.type === 'removed' && (
                    <span className="diff-val diff-val-before">{diff.before}</span>
                )}
                {diff.type === 'same' && (
                    <span className="diff-val diff-val-same">{diff.before}</span>
                )}
            </div>
        </div>
    );
}

export default MetadataDiff;
